import { useEffect, useMemo, useState } from 'react';
import EditorDialog from './EditorDialog';

/**
 * Parse suggested label into prefix + start number when possible (e.g. B12 → B + 12).
 */
export function splitSeatLabel(label) {
  const s = String(label || '').trim();
  const m = s.match(/^(.*?)(\d+)\s*$/);
  if (!m) return { prefix: s, start: 1 };
  return {
    prefix: m[1],
    start: Math.max(1, Number(m[2]) || 1),
  };
}

export function buildSpacedLabels(count, prefix, start, step) {
  const p = String(prefix ?? '');
  const s = Math.max(0, Math.floor(Number(start) || 0));
  const st = Math.max(1, Math.floor(Number(step) || 1));
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push(`${p}${s + i * st}`);
  }
  return out;
}

/**
 * Modal for manual seat naming — supports sequential / spaced numbers.
 */
export default function RenameSeatsModal({
  open,
  seatCount = 1,
  suggestedLabel = '',
  onClose,
  onApply,
}) {
  const multi = seatCount > 1;
  const initial = useMemo(() => splitSeatLabel(suggestedLabel), [suggestedLabel, open]);
  const [fullLabel, setFullLabel] = useState(suggestedLabel || '');
  const [prefix, setPrefix] = useState(initial.prefix);
  const [start, setStart] = useState(initial.start);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const parsed = splitSeatLabel(suggestedLabel);
    setFullLabel(String(suggestedLabel || '').trim());
    setPrefix(parsed.prefix);
    setStart(parsed.start);
    setStep(1);
    setError('');
  }, [open, suggestedLabel]);

  const preview = useMemo(() => {
    if (!multi) return fullLabel.trim() ? [fullLabel.trim()] : [];
    return buildSpacedLabels(Math.min(seatCount, 6), prefix, start, step);
  }, [multi, fullLabel, prefix, start, step, seatCount]);

  const handleApply = () => {
    if (!multi) {
      const label = String(fullLabel).trim().slice(0, 20);
      if (!label) {
        setError('İsim boş olamaz.');
        return;
      }
      onApply({ mode: 'single', labels: [label] });
      return;
    }
    const labels = buildSpacedLabels(seatCount, prefix, start, step).map((l) => l.slice(0, 20));
    if (labels.some((l) => !String(l).trim())) {
      setError('Önek veya başlangıç numarası gerekli.');
      return;
    }
    onApply({ mode: 'sequence', labels, prefix, start, step });
  };

  return (
    <EditorDialog
      open={open}
      title={multi ? `${seatCount} koltuğa isim ver` : 'Koltuk ismi'}
      message={
        multi
          ? 'Önek + başlangıç + aralık ile numaralandır. Örnek: VIP, 1, 2 → VIP1, VIP3, VIP5…'
          : 'Elle bir isim yaz (ör. VIP-A, B12).'
      }
      confirmLabel="Uygula"
      cancelLabel="İptal"
      showCancel
      onCancel={onClose}
      onConfirm={handleApply}
    >
      <div className="mt-4 space-y-3">
        {!multi ? (
          <label className="block text-xs font-medium text-gray-600">
            İsim
            <input
              autoFocus
              value={fullLabel}
              onChange={(e) => {
                setFullLabel(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply();
              }}
              maxLength={20}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#AE256D]/40"
              placeholder="B1"
            />
          </label>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <label className="block text-xs font-medium text-gray-600 col-span-1">
              Önek
              <input
                autoFocus
                value={prefix}
                onChange={(e) => {
                  setPrefix(e.target.value);
                  setError('');
                }}
                maxLength={12}
                className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#AE256D]/40"
                placeholder="B / VIP"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Başlangıç
              <input
                type="number"
                min={0}
                value={start}
                onChange={(e) => {
                  setStart(Math.max(0, Number(e.target.value) || 0));
                  setError('');
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#AE256D]/40"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Aralık
              <input
                type="number"
                min={1}
                value={step}
                onChange={(e) => {
                  setStep(Math.max(1, Number(e.target.value) || 1));
                  setError('');
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#AE256D]/40"
              />
            </label>
          </div>
        )}

        {preview.length > 0 && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
              Önizleme
            </div>
            <div className="text-sm text-gray-800 font-medium tracking-wide">
              {preview.join(' · ')}
              {multi && seatCount > preview.length ? ' · …' : ''}
            </div>
          </div>
        )}

        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    </EditorDialog>
  );
}
